import { StageFilterBar, useStageFilter, filterByStage } from '../common/StageFilterBar.tsx';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Kanban,
  LayoutGrid,
  List,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  Plus,
  Users,
  Settings,
  ChevronLeft,
  Layout,
  Zap,
  Archive,
  FileSpreadsheet,
  ChevronDown,
  Check,
  Star,
  Search,
  Filter,
  X,
  Copy,
  Lock,
  Globe,
  Trash2,
  Palette,
  UserPlus,
  Layers,
  Package,
  ShieldAlert,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import {
  Project,
  Task,
  TaskStatus,
  TaskPriority,
  User,
  ProjectBuild,
  ProjectFeature,
  BuildType,
  PriorityLevel,
  FeatureStatus,
} from '../../../shared/types.ts';
import { StatusBadge, PriorityBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import { ProjectMultiSelect } from '../common/ProjectMultiSelect.tsx';
import { TeamSelect, useTeamMemberIds, taskInTeams } from '../common/TeamSelect.tsx';
import { KanbanBoard, TRELLO_LABEL_PALETTE } from './KanbanBoard.tsx';
import { TaskTable } from './TaskTable.tsx';
import { TimelineView } from './TimelineView.tsx';
import { CalendarView } from './CalendarView.tsx';
import { ProjectFilesView } from './ProjectFilesView.tsx';
import { WhiteboardView } from './WhiteboardView.tsx';
import { AutomationSettingsModal } from './AutomationSettingsModal.tsx';
import { TaskDetailModal } from '../tasks/TaskDetailModal.tsx';
import { CreateTaskModal } from '../tasks/CreateTaskModal.tsx';
import { ImportTasksModal } from '../tasks/ImportTasksModal.tsx';
import { ExistingProjectMembersModal } from './ExistingProjectMembersModal.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface ProjectDetailViewProps {
  projectId: string;
  onBack: () => void;
  onSelectProject?: (id: string) => void;
  initialTaskId?: string | null;
}

type ProjectTab = 'kanban' | 'features' | 'builds' | 'list' | 'timeline' | 'whiteboard' | 'calendar' | 'files';

export const BOARD_THEMES: Record<
  string,
  { label: string; preview: string; canvasClass: string }
> = {
  default: {
    label: 'Default Clean',
    preview: 'bg-surface-100 dark:bg-surface-800',
    canvasClass: '',
  },
  ocean: {
    label: 'Ocean Breeze',
    preview: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    canvasClass:
      'bg-gradient-to-br from-blue-500/10 via-cyan-500/5 to-indigo-500/10 dark:from-blue-950/40 dark:via-cyan-950/20 dark:to-indigo-950/40 p-2 rounded-2xl border border-blue-500/15',
  },
  emerald: {
    label: 'Emerald Forest',
    preview: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    canvasClass:
      'bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-cyan-500/10 dark:from-emerald-950/40 dark:via-teal-950/20 dark:to-cyan-950/40 p-2 rounded-2xl border border-emerald-500/15',
  },
  sunset: {
    label: 'Sunset Glow',
    preview: 'bg-gradient-to-r from-rose-500 to-amber-500',
    canvasClass:
      'bg-gradient-to-br from-rose-500/10 via-amber-500/5 to-orange-500/10 dark:from-rose-950/40 dark:via-amber-950/20 dark:to-orange-950/40 p-2 rounded-2xl border border-rose-500/15',
  },
  royal: {
    label: 'Royal Violet',
    preview: 'bg-gradient-to-r from-violet-600 to-fuchsia-500',
    canvasClass:
      'bg-gradient-to-br from-violet-500/10 via-purple-500/5 to-fuchsia-500/10 dark:from-violet-950/40 dark:via-purple-950/20 dark:to-fuchsia-950/40 p-2 rounded-2xl border border-violet-500/15',
  },
  slate: {
    label: 'Midnight Slate',
    preview: 'bg-gradient-to-r from-slate-700 to-zinc-600',
    canvasClass:
      'bg-gradient-to-br from-slate-500/15 via-zinc-500/10 to-stone-500/15 dark:from-slate-900/70 dark:via-zinc-900/50 dark:to-stone-900/70 p-2 rounded-2xl border border-slate-500/20',
  },
};

export const ProjectDetailView: React.FC<ProjectDetailViewProps> = ({
  projectId,
  onBack,
  onSelectProject,
  initialTaskId,
}) => {
  const { token, user } = useAuth();
  const { can } = usePermissions();
  const { joinProject, leaveProject, subscribe } = useRealtime();
  const [project, setProject] = useState<Project | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const teamMemberIds = useTeamMemberIds(selectedTeamIds);
  // More projects shown on the same board next to this one (empty = just this project)
  const [extraProjectIds, setExtraProjectIds] = useState<string[]>([]);
  const keepExtrasRef = useRef(false);
  const combinedProjectIds = useMemo(
    () => [projectId, ...extraProjectIds.filter((id) => id !== projectId)].filter(Boolean) as string[],
    [projectId, extraProjectIds]
  );
  const isCombined = combinedProjectIds.length > 1;
  const combinedKey = combinedProjectIds.join(',');
  const inSelection = (id?: string | null) => !!id && combinedProjectIds.includes(id);
  const [activeTab, setActiveTab] = useState<ProjectTab>('kanban');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [createInitialStatus, setCreateInitialStatus] = useState<TaskStatus>('todo');
  const [isAutomationsOpen, setIsAutomationsOpen] = useState(false);
  const [isImportTasksOpen, setIsImportTasksOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Trello Board Filter & Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterMemberId, setFilterMemberId] = useState<string>('');
  const [filterLabelColor, setFilterLabelColor] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterDueDate, setFilterDueDate] = useState<string>('');

  // Board Settings Modal State
  const [isBoardSettingsOpen, setIsBoardSettingsOpen] = useState(false);
  useEscapeKey(isBoardSettingsOpen, () => setIsBoardSettingsOpen(false));
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editColor, setEditColor] = useState('#3b82f6');
  const [editTheme, setEditTheme] = useState('default');
  const [editVisibility, setEditVisibility] = useState<'workspace' | 'private' | 'public'>('workspace');
  const [editMemberIds, setEditMemberIds] = useState<string[]>([]);
  const [editRequireReviewApproval, setEditRequireReviewApproval] = useState(true);
  const [isSavingBoard, setIsSavingBoard] = useState(false);

  // Builds & Features state (Items 7, 18)
  const [builds, setBuilds] = useState<ProjectBuild[]>([]);
  const [features, setFeatures] = useState<ProjectFeature[]>([]);

  // Add Member Modal state (Items 5, 16)
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isViewMembersModalOpen, setIsViewMembersModalOpen] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [memberError, setMemberError] = useState<string | null>(null);
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);

  // Add Feature Modal state
  const [isAddFeatureModalOpen, setIsAddFeatureModalOpen] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureCode, setNewFeatureCode] = useState('');
  const [newFeatureDesc, setNewFeatureDesc] = useState('');
  const [newFeatureBuildId, setNewFeatureBuildId] = useState('');
  const [newFeaturePriority, setNewFeaturePriority] = useState<PriorityLevel>('medium');
  const [newFeatureStatus, setNewFeatureStatus] = useState<FeatureStatus>('planned');
  const [isSubmittingFeature, setIsSubmittingFeature] = useState(false);

  // Add Build Modal state
  const [isAddBuildModalOpen, setIsAddBuildModalOpen] = useState(false);
  const [newBuildVersion, setNewBuildVersion] = useState('');
  const [newBuildType, setNewBuildType] = useState<BuildType>('build');
  const [newBuildDesc, setNewBuildDesc] = useState('');
  const [newBuildReleasedAt, setNewBuildReleasedAt] = useState('');
  const [isSubmittingBuild, setIsSubmittingBuild] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(true);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  // Which board is already on screen. Refreshing that same board (after a ticket is added or
  // changed) happens quietly in the background instead of replacing the board with a spinner.
  const loadedProjectIdRef = useRef<string | null>(null);

  const fetchProjectData = async () => {
    if (!token) return;
    if (!projectId) {
      setProject(null);
      setIsLoadingProject(false);
      setProjectLoadError('No board project selected. Please select a board from the sidebar.');
      return;
    }
    const isBackgroundRefresh = loadedProjectIdRef.current === projectId;
    if (!isBackgroundRefresh) {
      setIsLoadingProject(true);
      setProjectLoadError(null);
    }
    try {
      // 1. Fetch Project Details
      const projRes = await fetch(`/api/projects/${projectId}`, {
        headers: getAuthHeaders(),
      });
      if (projRes.ok) {
        const pData = await projRes.json();
        setProject(pData.project);
        setProjectLoadError(null);
        loadedProjectIdRef.current = projectId;
      } else {
        // A failed quiet refresh keeps the board on screen
        if (isBackgroundRefresh) return;
        const errData = await projRes.json().catch(() => ({}));
        setProject(null);
        setProjectLoadError(errData.error || 'Board workspace not found or has been archived.');
        setIsLoadingProject(false);
        return;
      }

      // 2. Fetch Project Tasks (regular vs archived/bin)
      const idsToLoad = combinedProjectIds.length > 0 ? combinedProjectIds : [projectId];
      const responses = await Promise.all(
        idsToLoad.map((id) =>
          fetch(showArchived ? `/api/tasks?projectId=${id}&isArchived=true` : `/api/tasks?projectId=${id}`, {
            headers: getAuthHeaders(),
          }).catch(() => null)
        )
      );
      // The main project must load; an extra one that fails just leaves its cards out
      if (responses[0]?.ok) {
        const loaded: Task[] = [];
        for (const r of responses) {
          if (r && r.ok) {
            const d = await r.json().catch(() => ({}));
            loaded.push(...(d.tasks || []));
          }
        }
        setTasks(loaded);

        // Open initial task if requested
        if (initialTaskId) {
          const match = loaded.find((t: Task) => t.id === initialTaskId);
          if (match) setSelectedTask(match);
        }
      }

      // 3. Fetch Builds & Features (Items 7, 18)
      fetch(`/api/projects/${projectId}/builds`, { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => setBuilds(d.builds || []))
        .catch(() => {});

      fetch(`/api/projects/${projectId}/features`, { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => setFeatures(d.features || []))
        .catch(() => {});
    } catch (err: any) {
      console.error('Failed to load project:', err);
      if (!isBackgroundRefresh) setProjectLoadError(err.message || 'Unable to connect to board service.');
    } finally {
      setIsLoadingProject(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId, token, showArchived, combinedKey]);

  // Going to another board from the sidebar starts with just that board; picking projects in the
  // project filter keeps the extra ones (it sets the flag before it switches the main project)
  useEffect(() => {
    if (keepExtrasRef.current) {
      keepExtrasRef.current = false;
      return;
    }
    setExtraProjectIds([]);
  }, [projectId]);

  // Mind map and files belong to one project, so a combined board falls back to the board view
  useEffect(() => {
    if (isCombined && (activeTab === 'whiteboard' || activeTab === 'files')) setActiveTab('kanban');
  }, [isCombined, activeTab]);

  // Synchronize board when space is switched
  useEffect(() => {
    const handleSpaceChanged = (e: any) => {
      if (!token) return;
      const targetSpaceId = e.detail?.spaceId;
      const headers = getAuthHeaders(targetSpaceId ? { 'x-space-id': targetSpaceId } : {});
      fetch('/api/projects', { headers })
        .then((r) => r.json())
        .then((data) => {
          if (data.projects && data.projects.length > 0 && onSelectProject) {
            onSelectProject(data.projects[0].id);
          }
        })
        .catch(() => {});
    };

    window.addEventListener('space-changed', handleSpaceChanged);
    return () => window.removeEventListener('space-changed', handleSpaceChanged);
  }, [token, onSelectProject]);

  useEffect(() => {
    if (projectId) {
      joinProject(projectId);
      return () => {
        leaveProject(projectId);
      };
    }
  }, [projectId]);

  // Real-time synchronization via DOM events (instant intra-tab) & WebSocket (cross-tab / cross-user)
  useEffect(() => {
    const handleTaskCreatedLocal = (e: any) => {
      const createdTask = e.detail;
      if (createdTask && inSelection(createdTask.projectId) && !showArchived) {
        setTasks((prev) => {
          if (prev.some((t) => t.id === createdTask.id)) return prev;
          return [createdTask, ...prev];
        });
        fetchProjectData();
      }
    };

    const handleTaskUpdatedLocal = (e: any) => {
      const updatedTask = e.detail;
      if (updatedTask && inSelection(updatedTask.projectId)) {
        setTasks((prev) =>
          prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t))
        );
        fetchProjectData();
      }
    };

    const handleTaskArchivedLocal = (e: any) => {
      const { taskId } = e.detail || {};
      if (taskId && !showArchived) {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        fetchProjectData();
      } else if (taskId && showArchived) {
        fetchProjectData();
      }
    };

    const handleTaskRestoredLocal = () => {
      fetchProjectData();
    };

    window.addEventListener('task-created', handleTaskCreatedLocal);
    window.addEventListener('task-updated', handleTaskUpdatedLocal);
    window.addEventListener('task-archived', handleTaskArchivedLocal);
    window.addEventListener('task-restored', handleTaskRestoredLocal);

    const unsubscribe = subscribe((event) => {
      if (event.type === 'task_created') {
        const createdTask = event.data;
        if (createdTask && inSelection(createdTask.projectId) && !showArchived) {
          setTasks((prev) => {
            if (prev.some((t) => t.id === createdTask.id)) return prev;
            return [createdTask, ...prev];
          });
          fetchProjectData();
        }
      } else if (event.type === 'task_updated') {
        const updatedTask = event.data;
        if (updatedTask && inSelection(updatedTask.projectId)) {
          setTasks((prev) =>
            prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t))
          );
          fetchProjectData();
        }
      } else if (event.type === 'task_archived') {
        const { taskId, projectId: pId } = event.data || {};
        if (!pId || inSelection(pId)) {
          if (!showArchived) {
            setTasks((prev) => prev.filter((t) => t.id !== taskId));
          }
          fetchProjectData();
        }
      } else if (
        event.type === 'task_restored' ||
        event.type === 'tasks_reordered' ||
        event.type === 'tasks_imported'
      ) {
        fetchProjectData();
      } else if (event.type === 'board_lists_updated' && event.data?.projectId === projectId) {
        setProject((prev) => (prev ? { ...prev, lists: event.data.lists } : prev));
      } else if (event.type === 'project_updated' && event.data?.id === projectId) {
        setProject((prev) => (prev ? { ...prev, ...event.data } : event.data));
      } else if (event.type === 'project_created') {
        setAllProjects((prev) => {
          if (prev.some((p) => p.id === event.data.id)) return prev;
          return [event.data, ...prev];
        });
      }
    });

    return () => {
      window.removeEventListener('task-created', handleTaskCreatedLocal);
      window.removeEventListener('task-updated', handleTaskUpdatedLocal);
      window.removeEventListener('task-archived', handleTaskArchivedLocal);
      window.removeEventListener('task-restored', handleTaskRestoredLocal);
      unsubscribe();
    };
  }, [projectId, token, showArchived, subscribe, combinedKey]);

  const fetchUsers = () => {
    if (!token) return;
    setIsLoadingAllUsers(true);
    fetch('/api/team/members', { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => {
        const list = data.members || data.users || [];
        setAllUsers(list);
      })
      .catch(() => {
        fetch('/api/users', { headers: getAuthHeaders() })
          .then((r) => r.json())
          .then((data) => setAllUsers(data.members || data.users || []))
          .catch(() => {});
      })
      .finally(() => setIsLoadingAllUsers(false));
  };

  const handleRemoveProjectMember = async (memberId: string) => {
    if (!token || !projectId) return;
    const res = await fetch(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setProject((prev) =>
        prev
          ? {
              ...prev,
              members: (prev.members || []).filter((m) => m.id !== memberId),
            }
          : null
      );
    } else {
      const errD = await res.json().catch(() => ({}));
      throw new Error(errD.error || 'Failed to remove member from project.');
    }
  };

  useEffect(() => {
    if (token) {
      fetch('/api/projects?allSpaces=true', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((data) => setAllProjects(data.projects || []))
        .catch(() => {});
      fetchUsers();
    }
  }, [token]);

  const handleToggleStar = async () => {
    if (!token || !project) return;
    const optimistic = !project.isStarred;
    setProject({ ...project, isStarred: optimistic });
    setAllProjects((prev) =>
      prev.map((p) => (p.id === project.id ? { ...p, isStarred: optimistic } : p))
    );
    try {
      const res = await fetch(`/api/projects/${project.id}/star`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProject((prev) => (prev ? { ...prev, isStarred: data.isStarred } : prev));
      }
    } catch (err) {
      console.error('Failed to star board:', err);
    }
  };

  const openBoardSettings = () => {
    if (!project) return;
    setEditName(project.name);
    setEditDesc(project.description || '');
    setEditColor(project.color || '#3b82f6');
    setEditTheme(project.backgroundTheme || 'default');
    setEditVisibility(project.visibility || 'workspace');
    setEditMemberIds((project.members || []).map((m) => m.id));
    setEditRequireReviewApproval(project.requireReviewApproval !== false);
    setIsBoardSettingsOpen(true);
  };

  const handleSaveBoardSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !project || !editName.trim()) return;
    setIsSavingBoard(true);
    try {
      const res = await fetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editName.trim(),
          description: editDesc,
          color: editColor,
          backgroundTheme: editTheme,
          visibility: editVisibility,
          memberIds: editMemberIds,
          requireReviewApproval: editRequireReviewApproval,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.project) {
          setProject(data.project);
        }
        setIsBoardSettingsOpen(false);
        fetchProjectData();
      }
    } catch (err) {
      console.error('Failed to save board settings:', err);
    } finally {
      setIsSavingBoard(false);
    }
  };

  const handleDuplicateBoard = async () => {
    if (!token || !project) return;
    try {
      const res = await fetch(`/api/projects/${project.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: `${project.name} (Copy)` }),
      });
      if (res.ok) {
        const data = await res.json();
        setIsBoardSettingsOpen(false);
        if (data.project && onSelectProject) {
          onSelectProject(data.project.id);
        }
      }
    } catch (err) {
      console.error('Failed to duplicate board:', err);
    }
  };

  const handleArchiveBoard = async () => {
    if (!token || !project) return;
    try {
      const res = await fetch(`/api/projects/${project.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isArchived: !project.isArchived }),
      });
      if (res.ok) {
        setIsBoardSettingsOpen(false);
        fetchProjectData();
      }
    } catch (err) {
      console.error('Failed to archive board:', err);
    }
  };

  const handleDeleteBoard = async () => {
    if (!token || !project) return;
    if (!window.confirm(`Are you sure you want to permanently delete "${project.name}" and all its lists and cards?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/projects/${project.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setIsBoardSettingsOpen(false);
        onBack();
      }
    } catch (err) {
      console.error('Failed to delete board:', err);
    }
  };

  const handleUpdateTaskStatus = async (
    taskId: string,
    newStatus: TaskStatus,
    listId?: string | null
  ) => {
    // Optimistic UI update
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? { ...t, status: newStatus, ...(listId !== undefined ? { listId } : {}) }
          : t
      )
    );

    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: newStatus,
          ...(listId !== undefined ? { listId } : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
      }
      fetchProjectData();
    } catch (err) {
      console.error('Failed to update task status:', err);
    }
  };

  const handleQuickCreate = async (
    status: TaskStatus,
    title: string,
    listId?: string | null
  ) => {
    if (!token || !title.trim()) return;
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          projectId,
          title: title.trim(),
          status,
          listId: listId || null,
          priority: 'medium',
          assigneeId: user?.id || null,
          assignorId: user?.id || null,
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          setTasks((prev) => {
            if (prev.some((t) => t.id === data.task.id)) return prev;
            return [data.task, ...prev];
          });
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        fetchProjectData();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to create task.');
      }
    } catch (err) {
      console.error('Failed to quick create task:', err);
    }
  };

  // Filtered tasks calculation for Trello Search & Filter bar
  const activeFilterCount =
    (searchQuery.trim() ? 1 : 0) +
    (filterMemberId ? 1 : 0) +
    (filterLabelColor ? 1 : 0) +
    (filterPriority ? 1 : 0) +
    (filterDueDate ? 1 : 0);

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterMemberId('');
    setFilterLabelColor('');
    setFilterPriority('');
    setFilterDueDate('');
  };

  const stageFilter = useStageFilter();

  const boardFilteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      // 0. Team filter: only the people of the picked teams
      if (!taskInTeams(t, teamMemberIds)) return false;

      // 1. Keyword search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = t.title.toLowerCase().includes(q);
        const matchDesc = (t.description || '').toLowerCase().includes(q);
        const matchCode = (t.taskCode || '').toLowerCase().includes(q);
        const matchTag = (t.tags || []).some((tag) => tag.toLowerCase().includes(q));
        const matchColoredLabel = (t.coloredLabels || []).some((l) =>
          (l.name || '').toLowerCase().includes(q)
        );
        const matchMember = (t.assignees || []).some((a) =>
          a.fullName.toLowerCase().includes(q)
        );
        if (
          !matchTitle &&
          !matchDesc &&
          !matchCode &&
          !matchTag &&
          !matchColoredLabel &&
          !matchMember
        ) {
          return false;
        }
      }

      // 2. Member filter
      if (filterMemberId) {
        if (filterMemberId === '__unassigned__') {
          if (t.assigneeId || (t.assignees && t.assignees.length > 0)) return false;
        } else {
          const isAssigned =
            t.assigneeId === filterMemberId ||
            (t.assignees && t.assignees.some((a) => a.id === filterMemberId));
          if (!isAssigned) return false;
        }
      }

      // 3. Colored label filter
      if (filterLabelColor) {
        const hasColor = (t.coloredLabels || []).some((l) => l.color === filterLabelColor);
        if (!hasColor) return false;
      }

      // 4. Priority filter
      if (filterPriority && t.priority !== filterPriority) {
        return false;
      }

      // 5. Due date filter
      if (filterDueDate) {
        const todayStr = new Date().toISOString().split('T')[0];
        if (filterDueDate === 'overdue') {
          if (!t.dueDate || t.dueDate >= todayStr || t.status === 'completed') return false;
        } else if (filterDueDate === 'due_today') {
          if (!t.dueDate || t.dueDate !== todayStr) return false;
        } else if (filterDueDate === 'no_date') {
          if (t.dueDate) return false;
        } else if (filterDueDate === 'completed') {
          if (t.status !== 'completed') return false;
        }
      }

      return true;
    });
  }, [tasks, searchQuery, filterMemberId, filterLabelColor, filterPriority, filterDueDate, teamMemberIds]);

  // Stage chips (To Do / In Progress / ...) and the hidden Overdue / Completed sections
  const filteredTasks = filterByStage(
    boardFilteredTasks,
    stageFilter.stage,
    stageFilter.hideOverdue,
    stageFilter.hideCompleted
  );

  const availableBoardUsers = useMemo(() => {
    const userMap = new Map<string, { id: string; fullName: string; count: number }>();
    let unassignedCount = 0;

    if (project?.members && project.members.length > 0) {
      project.members.forEach((m) => {
        if (m && m.id) {
          userMap.set(m.id, {
            id: m.id,
            fullName: m.fullName || 'Member',
            count: 0,
          });
        }
      });
    }

    // On a single project the list is just that project's members (same as the Workspace); people who
    // only appear as an assignee are listed when several projects are shown together
    const onlyMembers = !isCombined && Boolean(project?.members && project.members.length > 0);
    tasks.forEach((t) => {
      const hasAssignee = Boolean(t.assigneeId || (t.assignees && t.assignees.length > 0));
      if (!hasAssignee) {
        unassignedCount += 1;
      }
      if (t.assigneeId) {
        const existing = userMap.get(t.assigneeId);
        if (existing) {
          existing.count += 1;
        } else if (!onlyMembers) {
          userMap.set(t.assigneeId, {
            id: t.assigneeId,
            fullName: t.assigneeName || 'Assignee',
            count: 1,
          });
        }
      }
      if (t.assignees && t.assignees.length > 0) {
        t.assignees.forEach((a) => {
          const existing = userMap.get(a.id);
          if (existing) {
            existing.count += 1;
          } else if (!onlyMembers) {
            userMap.set(a.id, {
              id: a.id,
              fullName: a.fullName || 'Member',
              count: 1,
            });
          }
        });
      }
    });

    // With teams picked, only the people of those teams are listed (and unassigned cards are not shown)
    const people = Array.from(userMap.values());
    return {
      list: teamMemberIds ? people.filter((u) => teamMemberIds.has(u.id)) : people,
      unassignedCount: teamMemberIds ? 0 : unassignedCount,
    };
  }, [tasks, project?.members, isCombined, teamMemberIds]);

  if (projectLoadError) {
    return (
      <div className="p-8 sm:p-12 text-center bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-card max-w-xl mx-auto my-8 space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto">
          <ShieldAlert className="w-7 h-7" />
        </div>
        <div>
          <h3 className="text-base font-bold text-surface-900 dark:text-surface-100">
            Board Workspace Unavailable
          </h3>
          <p className="text-xs text-surface-500 mt-1 max-w-md mx-auto">
            {projectLoadError}
          </p>
        </div>
        <div className="pt-2 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 transition-colors"
          >
            ← View All Projects
          </button>
          {onSelectProject && allProjects.length > 0 && allProjects[0].id !== projectId && (
            <button
              type="button"
              onClick={() => onSelectProject(allProjects[0].id)}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-brand-600 hover:bg-brand-700 text-white transition-colors shadow-sm"
            >
              Open {allProjects[0].name}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!project || project.id !== projectId || isLoadingProject) {
    return (
      <div className="p-12 text-center text-xs text-surface-400 flex flex-col items-center justify-center space-y-3">
        <div className="w-6 h-6 border-2 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
        <span>Loading board workspace...</span>
      </div>
    );
  }

  const tabs: Array<{ id: ProjectTab; label: string; icon: any }> = [
    { id: 'kanban', label: 'Kanban Board', icon: Kanban },
    { id: 'list', label: 'Grid / Spreadsheet', icon: List },
    { id: 'timeline', label: 'Gantt & Timeline', icon: Clock },
    { id: 'whiteboard', label: 'Mind Map & Canvas', icon: Layout },
    { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
    { id: 'files', label: 'Proofing & Assets', icon: FileText },
  ];

  const activeTheme = BOARD_THEMES[project?.backgroundTheme || 'default'] || BOARD_THEMES.default;

  return (
    <div
      className={
        activeTab === 'kanban'
          ? 'flex-1 min-h-0 flex flex-col gap-2 overflow-hidden'
          : 'space-y-4 pb-8 overflow-y-auto flex-1 min-h-0 pr-1'
      }
    >
      {/* Project Overview Header Card */}
      <div className="bg-white dark:bg-surface-900 rounded-2xl px-3 py-1.5 border border-surface-200 dark:border-surface-800 shadow-2xs shrink-0">
        {/* Top Row: Back Link, Board Title, Badges, Actions, Switcher */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-1.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                onClick={onBack}
                className="inline-flex items-center text-[11px] font-semibold text-surface-500 hover:text-brand-600 transition-colors mr-1"
                title="Back to Boards Portfolio"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-0.5" />
                <span>Boards</span>
              </button>
              <span className="text-surface-300 dark:text-surface-700">/</span>
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: project.color }}
              />
              <h2 className="text-sm sm:text-base font-black text-surface-900 dark:text-surface-50 tracking-tight truncate max-w-[260px] sm:max-w-md">
                {project.name}
              </h2>
              <button
                onClick={handleToggleStar}
                className={`p-1 rounded-lg border transition-all ${
                  project.isStarred
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
                    : 'bg-surface-50 dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-surface-400 hover:text-amber-500'
                }`}
                title={project.isStarred ? 'Unstar Board' : 'Star Board'}
              >
                <Star
                  className={`w-3 h-3 ${project.isStarred ? 'fill-amber-400 text-amber-400' : ''}`}
                />
              </button>
              <span className="font-mono text-[10px] font-bold text-brand-600 dark:text-brand-400 px-1.5 py-0.2 rounded bg-brand-500/10">
                {project.code || project.key}
              </span>
              <StatusBadge status={project.status} size="sm" />
              <PriorityBadge priority={project.priority} size="sm" />

              {/* Visibility Badge */}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded-full text-[10px] font-semibold bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700">
                {project.visibility === 'private' ? (
                  <>
                    <Lock className="w-2.5 h-2.5 text-amber-500" />
                    <span>Private</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-2.5 h-2.5 text-emerald-500" />
                    <span>Public</span>
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Quick Actions & Switcher */}
          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            {/* Quick Board Switcher */}
            {onSelectProject && allProjects.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setIsSwitcherOpen(!isSwitcherOpen)}
                  className="px-2.5 py-1 rounded-lg bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-[11px] font-semibold text-surface-700 dark:text-surface-300 flex items-center space-x-1.5 transition-colors border border-surface-200 dark:border-surface-700"
                  title="Switch Board"
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: project?.color || '#3b82f6' }}
                  />
                  <span className="font-bold">Switch Board</span>
                  <ChevronDown className="w-3 h-3 text-surface-400" />
                </button>

                {isSwitcherOpen && (
                  <div className="absolute right-0 top-full mt-1.5 w-64 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 py-2 divide-y divide-surface-100 dark:divide-surface-700 max-h-72 overflow-y-auto">
                    <div className="px-3 py-1 text-[10px] font-bold text-surface-400 uppercase tracking-wider">
                      Boards ({allProjects.length})
                    </div>
                    <div className="space-y-0.5 pt-1">
                      {allProjects.map((p) => {
                        const isCurrent = p.id === projectId;
                        return (
                          <div
                            key={p.id}
                            onClick={() => {
                              setIsSwitcherOpen(false);
                              if (p.id !== projectId) {
                                onSelectProject(p.id);
                              }
                            }}
                            className={`px-3 py-2 flex items-center justify-between cursor-pointer text-xs transition-colors ${
                              isCurrent
                                ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-bold'
                                : 'hover:bg-surface-50 dark:hover:bg-surface-700/40 text-surface-700 dark:text-surface-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2 truncate">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: p.color || '#3b82f6' }}
                              />
                              <span className="truncate">{p.name}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                              {p.isStarred && (
                                <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              )}
                              {isCurrent && <Check className="w-3.5 h-3.5 text-brand-600" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setIsViewMembersModalOpen(true)}
              className="px-2.5 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-[11px] font-semibold rounded-lg flex items-center space-x-1.5 transition-colors"
              title="Show Existing Members of this Project"
            >
              <Users className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
              <span>Members ({project.members?.length || 0})</span>
            </button>

            <button
              onClick={openBoardSettings}
              className="px-2.5 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-[11px] font-semibold rounded-lg flex items-center space-x-1 transition-colors"
              title="Board Settings, Theme, Visibility & Members"
            >
              <Settings className="w-3.5 h-3.5 text-surface-500" />
              <span className="hidden sm:inline">Settings</span>
            </button>

            <button
              onClick={() => setIsAutomationsOpen(true)}
              className="px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[11px] font-semibold rounded-lg flex items-center space-x-1 transition-colors"
            >
              <Zap className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Automations</span>
            </button>

            {can('task.create') && (
              <>
                <button
                  onClick={() => setIsImportTasksOpen(true)}
                  className="px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[11px] font-semibold rounded-lg flex items-center space-x-1 transition-colors"
                  title="Import tasks from Excel or CSV spreadsheet"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span className="hidden sm:inline">Import CSV</span>
                </button>

                <button
                  onClick={() => {
                    setCreateInitialStatus('todo');
                    setIsCreateTaskOpen(true);
                  }}
                  className="px-2.5 py-1 bg-brand-600 hover:bg-brand-700 text-white text-[11px] font-semibold rounded-lg shadow-2xs flex items-center space-x-1 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Card</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Project Meta Bar: Dates, Team, Progress, Members */}
        <div className="mt-2 pt-1.5 border-t border-surface-100 dark:border-surface-800 flex flex-wrap items-center justify-between gap-2 text-[11px]">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-surface-600 dark:text-surface-400">
            <div>
              <span className="text-surface-400 text-[10px] uppercase font-semibold mr-1">Start:</span>
              <span className="font-semibold text-surface-800 dark:text-surface-200">{project.startDate || '—'}</span>
            </div>
            <div>
              <span className="text-surface-400 text-[10px] uppercase font-semibold mr-1">Due:</span>
              <span className="font-semibold text-surface-800 dark:text-surface-200">{project.dueDate || '—'}</span>
            </div>
            <div>
              <span className="text-surface-400 text-[10px] uppercase font-semibold mr-1">Team:</span>
              <span className="font-semibold text-surface-800 dark:text-surface-200">
                {project.departmentName || 'General'}
              </span>
            </div>
          </div>

          {/* Progress Bar & Members */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 w-28 sm:w-36">
              <span className="text-[10px] text-surface-500 font-semibold shrink-0">
                {project.progressPercent || 0}%
              </span>
              <div className="w-full bg-surface-100 dark:bg-surface-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-brand-500 h-full rounded-full transition-all"
                  style={{ width: `${project.progressPercent || 0}%` }}
                />
              </div>
            </div>

            {/* Team Avatars + Existing Members Icon + Add Member Button */}
            <div className="flex items-center space-x-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setIsViewMembersModalOpen(true)}
                className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-300 border border-surface-200 dark:border-surface-700 transition-colors"
                title="View existing members of this project"
              >
                <Users className="w-3 h-3 text-brand-600 dark:text-brand-400" />
                <span>Existing Members ({project.members?.length || 0})</span>
              </button>

              {project.members && project.members.length > 0 && (
                <div
                  className="flex items-center -space-x-1 cursor-pointer"
                  onClick={() => setIsViewMembersModalOpen(true)}
                  title="Click to view all existing members"
                >
                  {project.members.slice(0, 5).map((m) => {
                    const isFiltered = filterMemberId === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterMemberId((prev) => (prev === m.id ? '' : m.id));
                        }}
                        className={`rounded-full transition-transform hover:scale-110 hover:z-10 ${
                          isFiltered ? 'ring-2 ring-brand-500 ring-offset-1 dark:ring-offset-surface-900 z-10' : ''
                        }`}
                        title={`Filter cards by ${m.fullName}`}
                      >
                        <Avatar src={m.avatarUrl} name={m.fullName} size="xs" />
                      </button>
                    );
                  })}
                  {project.members.length > 5 && (
                    <span className="w-5 h-5 rounded-full bg-surface-200 dark:bg-surface-700 text-[9px] font-bold text-surface-600 dark:text-surface-300 flex items-center justify-center border border-white dark:border-surface-900">
                      +{project.members.length - 5}
                    </span>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  fetchUsers();
                  setIsAddMemberModalOpen(true);
                }}
                className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/40 dark:hover:bg-brand-900/50 text-brand-600 dark:text-brand-300 border border-brand-200/60 dark:border-brand-800/60 transition-colors"
                title="Add Members to this Board"
              >
                <UserPlus className="w-3 h-3" />
                <span>+ Member</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stage chips with Hide / Show Overdue and Completed, same as the dashboard */}
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 px-0.5 shrink-0">
        <StageFilterBar
          tasks={boardFilteredTasks}
          stage={stageFilter.stage}
          onSelectStage={stageFilter.selectStage}
          hideOverdue={stageFilter.hideOverdue}
          hideCompleted={stageFilter.hideCompleted}
          onToggleHideOverdue={stageFilter.toggleHideOverdue}
          onToggleHideCompleted={stageFilter.toggleHideCompleted}
        />
      </div>

      {/* Workspace-Style View Switcher + Search, User Filter, Advanced Filter & Bin Controls */}
      <div className="bg-white dark:bg-[#181b20] rounded-2xl px-3 py-2 border border-[#d5d8de]/60 dark:border-surface-800 shadow-2xs flex flex-nowrap items-center justify-between gap-2 shrink-0">
        <div className="flex items-center space-x-2 min-w-0 shrink">
          <div className="w-7 h-7 rounded-xl bg-[#121417] text-brand-400 flex items-center justify-center shrink-0">
            {activeTab === 'kanban' && <LayoutGrid className="w-3.5 h-3.5" />}
            {activeTab === 'list' && <List className="w-3.5 h-3.5" />}
            {activeTab === 'timeline' && <Clock className="w-3.5 h-3.5" />}
            {activeTab === 'whiteboard' && <Layout className="w-3.5 h-3.5" />}
            {activeTab === 'calendar' && <CalendarIcon className="w-3.5 h-3.5" />}
            {activeTab === 'files' && <FileText className="w-3.5 h-3.5" />}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-extrabold text-sm text-[#121417] dark:text-white truncate min-w-0">
              {activeTab === 'kanban' && 'Sprint Kanban Board'}
              {activeTab === 'list' && 'Grid & Dense Spreadsheet'}
              {activeTab === 'timeline' && 'Interactive Gantt & Critical Path'}
              {activeTab === 'whiteboard' && 'Mind Map & Freeform Brainstorm Canvas'}
              {activeTab === 'calendar' && 'Sprint Milestones Calendar'}
              {activeTab === 'files' && 'Proofing & Assets'}
            </h3>
            <span className="text-[11px] font-bold px-2 py-0.2 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 shrink-0 min-w-[64px] text-center">
              {filteredTasks.length} cards
            </span>
          </div>
        </div>

        {/* Workspace-Style Controls + Extra Board Features (All on the same line) */}
        <div className="flex flex-nowrap items-center gap-1.5 min-w-0">
          {/* Instant Board Card Search (the one part that gives way when the screen is narrow) */}
          <div className="relative flex-1 min-w-[90px] w-36 sm:w-44 shrink">
            <Search className="w-3 h-3 text-[#667085] dark:text-surface-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter cards..."
              className="w-full pl-7 pr-6 py-1 rounded-xl bg-[#f4f5f7] dark:bg-surface-800 border border-[#d5d8de] dark:border-surface-700 text-[11px] font-bold text-[#121417] dark:text-white placeholder:text-[#667085] dark:placeholder:text-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </div>

          {/* View Selector Dropdown (Workspace style + Proofing & Assets) */}
          <div className="flex items-center space-x-1">
            <span className="text-[11px] font-bold text-[#667085] dark:text-[#9aa2b1]">View:</span>
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as ProjectTab)}
                className="text-[11px] font-black pl-2 pr-6 py-1 rounded-xl bg-[#121417] text-white dark:bg-white dark:text-black border border-transparent shadow-sm focus:outline-none cursor-pointer appearance-none"
                aria-label="Select Board View"
              >
                <option value="kanban">📌 Kanban Board</option>
                <option value="list">📊 Grid / Spreadsheet</option>
                <option value="timeline">⏱️ Gantt & Timeline</option>
                <option value="whiteboard" disabled={isCombined}>🧠 Mind Map & Canvas</option>
                <option value="calendar">📅 Calendar</option>
                <option value="files" disabled={isCombined}>📄 Proofing & Assets</option>
              </select>
              <ChevronDown className="w-3 h-3 text-brand-400 dark:text-brand-300 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none stroke-[2.5]" />
            </div>
          </div>

          {/* Team filter: the tasks of the people in one or more teams */}
          <TeamSelect selectedTeamIds={selectedTeamIds} onChange={setSelectedTeamIds} />

          {/* Project filter: one board, several at once, or all (same picker as the Workspace) */}
          {onSelectProject && allProjects.length > 0 && (
            <ProjectMultiSelect
              projects={allProjects.map((p) => ({ id: p.id, name: p.name, key: p.code || p.key }))}
              selectedIds={allProjects.length > 1 && combinedProjectIds.length === allProjects.length ? [] : combinedProjectIds}
              onChange={(ids) => {
                // Nothing ticked (or everything ticked) means all projects
                const next = ids.length === 0 ? allProjects.map((p) => p.id) : ids;
                const primary = next.includes(projectId) ? projectId : next[0];
                setExtraProjectIds(next.filter((id) => id !== primary));
                if (primary !== projectId) {
                  keepExtrasRef.current = true;
                  onSelectProject(primary);
                }
              }}
            />
          )}

          {/* Direct User-Wise Quick Filter (Workspace style) */}
          <div className="flex items-center space-x-1 bg-[#f4f5f7] dark:bg-surface-800 rounded-xl px-2 py-1 border border-[#d5d8de] dark:border-surface-700">
            <Users className="w-3 h-3 text-[#667085] dark:text-surface-400 shrink-0" />
            <select
              value={filterMemberId}
              onChange={(e) => setFilterMemberId(e.target.value)}
              className="bg-transparent text-[11px] font-bold text-[#121417] dark:text-white focus:outline-none cursor-pointer w-[150px] truncate"
              title="Filter tasks by user"
            >
              <option value="">
                All Users ({availableBoardUsers.list.length})
              </option>
              {user && (
                <option value={user.id}>
                  Assigned to Me ({tasks.filter((t) => t.assigneeId === user.id || (t.assignees && t.assignees.some((a) => a.id === user.id))).length})
                </option>
              )}
              {availableBoardUsers.unassignedCount > 0 && (
                <option value="__unassigned__">
                  Unassigned ({availableBoardUsers.unassignedCount})
                </option>
              )}
              {availableBoardUsers.list
                .filter((u) => !user || u.id !== user.id)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.count})
                  </option>
                ))}
            </select>
            {/* always takes its space, so picking a user does not move anything */}
            <button
              type="button"
              onClick={() => setFilterMemberId('')}
              className={`p-0.5 rounded text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 transition-colors ${
                filterMemberId ? '' : 'invisible'
              }`}
              title="Clear user filter"
              tabIndex={filterMemberId ? 0 : -1}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>

          {/* Filter Popover Button */}
          <div className="relative">
            <button
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className={`flex items-center space-x-1 px-2.5 py-1 rounded-xl text-[11px] font-bold transition-all border ${
                activeFilterCount > 0
                  ? 'bg-brand-600 text-white border-brand-600 shadow-2xs'
                  : 'bg-[#f4f5f7] dark:bg-surface-800 text-[#121417] dark:text-white border-[#d5d8de] dark:border-surface-700 hover:bg-surface-200 dark:hover:bg-surface-700'
              }`}
            >
              <Filter className="w-3 h-3" />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="px-1 py-0.2 rounded-full bg-white/20 text-[10px]">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {isFilterOpen && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated z-30 p-4 space-y-3 text-xs">
                <div className="flex items-center justify-between border-b border-surface-100 dark:border-surface-700 pb-2">
                  <span className="font-bold text-surface-800 dark:text-surface-100">
                    Filter Board Cards
                  </span>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="text-surface-400 hover:text-surface-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Member Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                    Members
                  </label>
                  <select
                    value={filterMemberId}
                    onChange={(e) => setFilterMemberId(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-xs"
                  >
                    <option value="">All Members</option>
                    {user && <option value={user.id}>Assigned to Me ({user.fullName})</option>}
                    <option value="__unassigned__">No Members (Unassigned)</option>
                    {(project.members || []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Colored Label Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                    Label Color
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {TRELLO_LABEL_PALETTE.map((lbl) => (
                      <button
                        key={lbl.color}
                        type="button"
                        onClick={() =>
                          setFilterLabelColor((prev) =>
                            prev === lbl.color ? '' : lbl.color
                          )
                        }
                        className={`w-6 h-5 rounded-md transition-transform ${lbl.dot} ${
                          filterLabelColor === lbl.color
                            ? 'ring-2 ring-offset-1 ring-surface-900 dark:ring-white scale-110'
                            : 'opacity-80 hover:opacity-100'
                        }`}
                        title={lbl.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                    Priority
                  </label>
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-xs"
                  >
                    <option value="">Any Priority</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Due Date Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-surface-400 mb-1">
                    Due Date Status
                  </label>
                  <select
                    value={filterDueDate}
                    onChange={(e) => setFilterDueDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-xs"
                  >
                    <option value="">All Dates</option>
                    <option value="overdue">Overdue</option>
                    <option value="due_today">Due Today</option>
                    <option value="no_date">No Due Date</option>
                    <option value="completed">Completed Cards</option>
                  </select>
                </div>

                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="w-full py-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-200 font-semibold text-xs transition-colors"
                  >
                    Clear All Filters ({activeFilterCount})
                  </button>
                )}
              </div>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="px-2 py-1 rounded-xl bg-brand-50 dark:bg-brand-950/50 text-brand-600 dark:text-brand-400 text-[11px] font-bold flex items-center gap-1 hover:underline"
            >
              <span>
                Showing {filteredTasks.length}/{tasks.length}
              </span>
              <X className="w-3 h-3" />
            </button>
          )}

          {/* Bin / Archive Toggle Button */}
          <button
            onClick={() => setShowArchived((prev) => !prev)}
            className={`flex items-center space-x-1 px-2.5 py-1 rounded-xl text-[11px] font-bold transition-all border shrink-0 ${
              showArchived
                ? 'bg-amber-500 text-white border-amber-500 shadow-2xs'
                : 'bg-[#f4f5f7] dark:bg-surface-800 text-[#121417] dark:text-white hover:bg-surface-200 dark:hover:bg-surface-700 border-[#d5d8de] dark:border-surface-700'
            }`}
            title="Toggle viewing soft-deleted tasks in the Bin / Archive"
          >
            <Archive className="w-3 h-3" />
            <span>{showArchived ? 'Viewing Bin' : 'Bin'}</span>
          </button>
        </div>
      </div>

      {/* Bin / Archive Mode Banner */}
      {showArchived && (
        <div className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between text-xs text-amber-800 dark:text-amber-300 shrink-0">
          <div className="flex items-center space-x-2">
            <Archive className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span>
              <strong>Viewing Bin / Archive:</strong> Click any card to review audit logs, restore it, or permanently delete it.
            </span>
          </div>
          <button
            onClick={() => setShowArchived(false)}
            className="text-xs font-bold underline hover:text-amber-900 dark:hover:text-amber-100"
          >
            Exit Bin
          </button>
        </div>
      )}

      {/* Active Tab View Body */}
      <div
        className={
          activeTab === 'kanban'
            ? `flex-1 min-h-0 flex flex-col overflow-hidden ${activeTheme.canvasClass}`
            : ''
        }
      >
        {activeTab === 'kanban' && (
          <KanbanBoard
            projectId={isCombined ? undefined : projectId}
            lists={isCombined ? undefined : project.lists}
            members={isCombined ? undefined : project.members}
            tasks={filteredTasks}
            onTaskClick={(task) => setSelectedTask(task)}
            onUpdateTaskStatus={handleUpdateTaskStatus}
            onQuickCreateTask={handleQuickCreate}
            onListsChange={(lists) =>
              setProject((prev) => (prev ? { ...prev, lists } : prev))
            }
            onRefreshTasks={fetchProjectData}
            hideUserFilter={true}
            hideOverdue={stageFilter.hideOverdue}
            hideCompleted={stageFilter.hideCompleted}
          />
        )}

        {activeTab === 'features' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-surface-200 dark:border-surface-800">
              <h3 className="text-sm font-extrabold text-surface-900 dark:text-surface-100 flex items-center space-x-2">
                <Layers className="w-4 h-4 text-indigo-500" />
                <span>Features & Modules ({features.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsAddFeatureModalOpen(true)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 text-[11px] font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Feature</span>
              </button>
            </div>

            {features.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-surface-850 rounded-2xl border border-surface-200 dark:border-surface-700/60 space-y-3">
                <Layers className="w-10 h-10 text-surface-400 mx-auto" />
                <h4 className="text-sm font-bold text-surface-800 dark:text-surface-200">No Features Created Yet</h4>
                <p className="text-xs text-surface-500 max-w-md mx-auto">
                  Features group and organize tasks into functional modules and connect them to release builds.
                </p>
                <button
                  type="button"
                  onClick={() => setIsAddFeatureModalOpen(true)}
                  className="inline-flex items-center space-x-1.5 px-4 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add First Feature</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feat) => {
                  const featTasks = tasks.filter((t) => t.featureId === feat.id);
                  const completedCount = featTasks.filter((t) => t.status === 'done').length;
                  const percent = featTasks.length > 0 ? Math.round((completedCount / featTasks.length) * 100) : 0;
                  const matchingBuild = builds.find((b) => b.id === feat.buildId);

                  return (
                    <div
                      key={feat.id}
                      className="p-4 rounded-2xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/70 dark:border-surface-700/60 shadow-neo-card hover:shadow-neo transition-all space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          {feat.code && (
                            <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60">
                              {feat.code}
                            </span>
                          )}
                          <h4 className="text-sm font-bold text-surface-900 dark:text-surface-100 mt-1">
                            {feat.name}
                          </h4>
                        </div>
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300">
                          {feat.status}
                        </span>
                      </div>

                      {feat.description && (
                        <p className="text-xs text-surface-500 line-clamp-2">{feat.description}</p>
                      )}

                      {matchingBuild && (
                        <div className="flex items-center space-x-1.5 text-xs text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 px-2 py-1 rounded-lg border border-sky-200/60 dark:border-sky-800/60">
                          <Package className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                          <span className="font-medium truncate">Build: {matchingBuild.version}</span>
                        </div>
                      )}

                      {/* Tasks Progress */}
                      <div className="space-y-1 pt-1">
                        <div className="flex items-center justify-between text-[11px] text-surface-500 font-medium">
                          <span>Tasks ({completedCount}/{featTasks.length})</span>
                          <span>{percent}%</span>
                        </div>
                        <div className="w-full bg-surface-100 dark:bg-surface-800 h-1.5 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-500 h-full rounded-full transition-all"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>

                      {/* Feature Actions */}
                      <div className="pt-2 border-t border-surface-100 dark:border-surface-800 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab('kanban');
                          }}
                          className="text-xs text-brand-600 dark:text-brand-400 font-semibold hover:underline"
                        >
                          View in Kanban Board &rarr;
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Delete feature "${feat.name}"?`)) return;
                            try {
                              const res = await fetch(`/api/projects/${projectId}/features/${feat.id}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (res.ok) {
                                setFeatures((prev) => prev.filter((f) => f.id !== feat.id));
                              } else {
                                const errD = await res.json().catch(() => ({}));
                                alert(errD.error || 'Failed to delete feature. Only admin, manager, or project lead can delete features.');
                              }
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          className="p-1 text-surface-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                          title="Delete Feature"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'builds' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-surface-200 dark:border-surface-800">
              <h3 className="text-sm font-extrabold text-surface-900 dark:text-surface-100 flex items-center space-x-2">
                <Package className="w-4 h-4 text-sky-500" />
                <span>Builds, Releases & Hotfixes ({builds.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsAddBuildModalOpen(true)}
                className="inline-flex items-center space-x-1 px-3 py-1.5 text-[11px] font-bold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Build / Release</span>
              </button>
            </div>

            {builds.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-surface-850 rounded-2xl border border-surface-200 dark:border-surface-700/60 space-y-3">
                <Package className="w-10 h-10 text-surface-400 mx-auto" />
                <h4 className="text-sm font-bold text-surface-800 dark:text-surface-200">No Builds Configured</h4>
                <p className="text-xs text-surface-500 max-w-md mx-auto">
                  Create a version release or hotfix to package features and assign sprint goals.
                </p>
                <button
                  type="button"
                  onClick={() => setIsAddBuildModalOpen(true)}
                  className="inline-flex items-center space-x-1.5 px-4 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Create First Build</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {builds.map((b) => {
                  const buildFeatures = features.filter((f) => f.buildId === b.id);
                  const isReleased = b.status === 'released';

                  return (
                    <div
                      key={b.id}
                      className="p-4 rounded-2xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/70 dark:border-surface-700/60 shadow-neo-card hover:shadow-neo transition-all space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                            b.buildType === 'hotfix'
                              ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                              : b.buildType === 'release'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                              : 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                          }`}>
                            {b.buildType}
                          </span>
                          <h4 className="text-base font-extrabold text-surface-900 dark:text-surface-100 mt-1 font-mono">
                            {b.version}
                          </h4>
                        </div>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                          isReleased
                            ? 'bg-emerald-500 text-white'
                            : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300'
                        }`}>
                          {b.status}
                        </span>
                      </div>

                      {b.description && (
                        <p className="text-xs text-surface-500 line-clamp-2">{b.description}</p>
                      )}

                      <div className="p-2.5 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/50 dark:border-surface-700/50 text-xs space-y-1">
                        <div className="flex items-center justify-between text-surface-500">
                          <span>Scoped Features:</span>
                          <span className="font-bold text-surface-800 dark:text-surface-200">{buildFeatures.length}</span>
                        </div>
                        {b.releasedAt && (
                          <div className="flex items-center justify-between text-surface-500 text-[11px]">
                            <span>Released:</span>
                            <span className="font-mono">{new Date(b.releasedAt).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      {/* Build Actions */}
                      <div className="pt-2 border-t border-surface-100 dark:border-surface-800 flex items-center justify-between">
                        {!isReleased ? (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await fetch(`/api/projects/${projectId}/builds/${b.id}`, {
                                  method: 'PUT',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${token}`,
                                  },
                                  body: JSON.stringify({ status: 'released', releasedAt: new Date().toISOString() }),
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setBuilds((prev) => prev.map((item) => (item.id === b.id ? data.build : item)));
                                }
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            className="text-xs text-emerald-600 dark:text-emerald-400 font-bold hover:underline"
                          >
                            Mark as Released
                          </button>
                        ) : (
                          <span className="text-xs text-emerald-600 font-semibold flex items-center space-x-1">
                            <Check className="w-3.5 h-3.5" />
                            <span>Released</span>
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Delete build "${b.version}"?`)) return;
                            try {
                              const res = await fetch(`/api/projects/${projectId}/builds/${b.id}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (res.ok) {
                                setBuilds((prev) => prev.filter((item) => item.id !== b.id));
                              } else {
                                const errD = await res.json().catch(() => ({}));
                                alert(errD.error || 'Failed to delete build. Only admin, manager, or project lead can delete builds.');
                              }
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          className="p-1 text-surface-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                          title="Delete Build"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'list' && (
          <TaskTable
            tasks={filteredTasks}
            onTaskClick={(task) => setSelectedTask(task)}
            onUpdateTaskStatus={handleUpdateTaskStatus}
            onQuickCreateTask={handleQuickCreate}
          />
        )}

        {activeTab === 'timeline' && (
          <TimelineView
            tasks={filteredTasks}
            projectId={projectId}
            onTaskClick={(task) => setSelectedTask(task)}
          />
        )}

        {activeTab === 'whiteboard' && (
          <WhiteboardView
            projectId={projectId}
            tasks={filteredTasks}
            onTaskClick={(task) => setSelectedTask(task)}
          />
        )}

        {activeTab === 'calendar' && (
          <CalendarView
            tasks={filteredTasks}
            onTaskClick={(task) => setSelectedTask(task)}
          />
        )}

        {activeTab === 'files' && <ProjectFilesView projectId={projectId} />}
      </div>

      {/* Board Settings, Customization & Members Modal */}
      {isBoardSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-2xl w-full max-w-lg shadow-elevated overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Settings className="w-4 h-4 text-brand-600" />
                <h3 className="text-sm font-bold text-surface-900 dark:text-white">
                  Board Settings & Customization
                </h3>
              </div>
              <button
                onClick={() => setIsBoardSettingsOpen(false)}
                className="text-surface-400 hover:text-surface-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveBoardSettings} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto text-xs">
              <div>
                <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1">
                  Board Title
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-white font-semibold"
                />
              </div>

              <div>
                <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-white"
                />
              </div>

              {/* Board Visibility */}
              <div>
                <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1.5">
                  Board Visibility
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'workspace', label: 'Workspace', icon: Users, desc: 'All team members' },
                    { id: 'private', label: 'Private', icon: Lock, desc: 'Only board members' },
                    { id: 'public', label: 'Public', icon: Globe, desc: 'Organization-wide' },
                  ].map((vis) => {
                    const Icon = vis.icon;
                    const active = editVisibility === vis.id;
                    return (
                      <button
                        key={vis.id}
                        type="button"
                        onClick={() => setEditVisibility(vis.id as any)}
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          active
                            ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300'
                            : 'border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold">
                          <Icon className="w-3.5 h-3.5" />
                          <span>{vis.label}</span>
                        </div>
                        <div className="text-[10px] text-surface-400 mt-0.5">{vis.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Board Background Theme */}
              <div>
                <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1.5 flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5 text-brand-500" />
                  <span>Board Background Theme</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(BOARD_THEMES).map(([key, theme]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setEditTheme(key)}
                      className={`p-2 rounded-xl border text-left transition-all flex items-center gap-2 ${
                        editTheme === key
                          ? 'border-brand-500 ring-2 ring-brand-500/20 font-bold'
                          : 'border-surface-200 dark:border-surface-700'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-lg shrink-0 ${theme.preview}`} />
                      <span className="truncate text-[11px]">{theme.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Board Members Management */}
              <div>
                <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1.5">
                  Board Members ({editMemberIds.length})
                </label>
                <div className="max-h-36 overflow-y-auto border border-surface-200 dark:border-surface-700 rounded-xl divide-y divide-surface-100 dark:divide-surface-800">
                  {allUsers.map((u) => {
                    const isMember = editMemberIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800/60"
                      >
                        <div className="flex items-center space-x-2">
                          <Avatar src={u.avatarUrl} name={u.fullName} size="sm" />
                          <div>
                            <div className="font-semibold text-surface-800 dark:text-surface-200">
                              {u.fullName}
                            </div>
                            <div className="text-[10px] text-surface-400">{u.jobTitle || u.role}</div>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={isMember}
                          onChange={() =>
                            setEditMemberIds((prev) =>
                              prev.includes(u.id)
                                ? prev.filter((id) => id !== u.id)
                                : [...prev, u.id]
                            )
                          }
                          className="rounded border-surface-300 text-brand-600 focus:ring-brand-500"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Approval Workflow Policy */}
              <div className="p-3 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200 dark:border-surface-700 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5 pr-2">
                    <div className="font-bold text-xs text-surface-900 dark:text-surface-100 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-amber-500" />
                      <span>Require Review & Approval for Completion</span>
                    </div>
                    <div className="text-[11px] text-surface-500 dark:text-surface-400">
                      When enabled, only Team Leads, Workspace Admins, or the task Assignor can approve tasks into Completed.
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={editRequireReviewApproval}
                      onChange={(e) => setEditRequireReviewApproval(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-surface-300 peer-focus:outline-none rounded-full peer dark:bg-surface-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-surface-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-600"></div>
                  </label>
                </div>
              </div>

              {/* Board Actions: Duplicate, Archive, Delete */}
              <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDuplicateBoard}
                    className="px-3 py-1.5 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 text-surface-700 dark:text-surface-300 font-semibold flex items-center gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Duplicate Board</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleArchiveBoard}
                    className="px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1.5"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    <span>{project.isArchived ? 'Restore Board' : 'Archive'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteBoard}
                    className="px-2.5 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1"
                    title="Delete Board"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsBoardSettingsOpen(false)}
                    className="px-3 py-1.5 rounded-xl text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingBoard}
                    className="px-4 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold shadow-sm disabled:opacity-50"
                  >
                    {isSavingBoard ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          taskId={selectedTask.id}
          isOpen={true}
          onClose={() => setSelectedTask(null)}
          onTaskUpdated={() => fetchProjectData()}
          projectMembers={project?.members}
        />
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        projectId={projectId}
        initialStatus={createInitialStatus}
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        onTaskCreated={() => fetchProjectData()}
        groupMembers={project?.members}
      />

      {/* Automation Settings Modal */}
      <AutomationSettingsModal
        projectId={projectId}
        isOpen={isAutomationsOpen}
        onClose={() => setIsAutomationsOpen(false)}
      />

      {/* Import Tasks Modal */}
      <ImportTasksModal
        projectId={projectId}
        projects={project ? [project] : []}
        isOpen={isImportTasksOpen}
        onClose={() => setIsImportTasksOpen(false)}
        onTasksImported={() => fetchProjectData()}
      />

      {/* Existing Project Members Modal */}
      {isViewMembersModalOpen && project && (
        <ExistingProjectMembersModal
          isOpen={isViewMembersModalOpen}
          onClose={() => setIsViewMembersModalOpen(false)}
          projectName={project.name}
          members={project.members || []}
          onAddMemberClick={() => {
            fetchUsers();
            setIsAddMemberModalOpen(true);
          }}
          onFilterByMember={(memberId) => setFilterMemberId(memberId)}
          onRemoveMember={handleRemoveProjectMember}
          canManageMembers={can('project.members') || project?.ownerId === user?.id}
        />
      )}

      {/* 1. Add Member Modal (Items 5 & 16) */}
      {isAddMemberModalOpen && (
        <Modal
          isOpen={isAddMemberModalOpen}
          onClose={() => {
            setIsAddMemberModalOpen(false);
            setMemberError(null);
            setMemberSearchQuery('');
          }}
          title={`Manage Project Members • ${project?.name}`}
          maxWidth="md"
        >
          <div className="space-y-4">
            <p className="text-xs text-surface-500">
              Add colleagues to this project so they can be assigned tasks, view board status, and collaborate.
            </p>

            {memberError && (
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-xs text-rose-600 font-medium">
                {memberError}
              </div>
            )}

            <div className="relative">
              <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search colleagues by name or email..."
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {isLoadingAllUsers && allUsers.length === 0 ? (
                <div className="py-8 text-center text-xs text-surface-400 flex flex-col items-center justify-center space-y-2">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
                  <span>Loading team members...</span>
                </div>
              ) : allUsers.filter((u) => {
                  if (!memberSearchQuery.trim()) return true;
                  const q = memberSearchQuery.toLowerCase();
                  return (
                    (u.fullName && u.fullName.toLowerCase().includes(q)) ||
                    (u.email && u.email.toLowerCase().includes(q))
                  );
                }).length === 0 ? (
                <div className="py-8 text-center text-xs text-surface-400 space-y-2">
                  <p>
                    {memberSearchQuery.trim()
                      ? `No colleagues found matching "${memberSearchQuery}".`
                      : 'No colleagues found in workspace.'}
                  </p>
                  <button
                    type="button"
                    onClick={fetchUsers}
                    className="text-brand-600 hover:text-brand-700 underline text-xs font-semibold"
                  >
                    Refresh member list
                  </button>
                </div>
              ) : (
                allUsers
                  .filter((u) => {
                    if (!memberSearchQuery.trim()) return true;
                    const q = memberSearchQuery.toLowerCase();
                    return (
                      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
                      (u.email && u.email.toLowerCase().includes(q))
                    );
                  })
                  .map((u) => {
                    const isAlreadyMember = (project?.members || []).some((m) => m.id === u.id);

                  return (
                    <div
                      key={u.id}
                      className="p-2.5 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-700/60 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                        <Avatar src={u.avatarUrl} name={u.fullName} size="sm" />
                        <div className="min-w-0 truncate">
                          <span className="font-semibold text-surface-900 dark:text-surface-100 block truncate">
                            {u.fullName}
                          </span>
                          <span className="text-[10px] text-surface-400 block truncate">
                            {u.email || u.role}
                          </span>
                        </div>
                      </div>

                      {isAlreadyMember ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-200/60 dark:border-emerald-800/60">
                            Member
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                setMemberError(null);
                                const res = await fetch(`/api/projects/${projectId}/members/${u.id}`, {
                                  method: 'DELETE',
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (res.ok) {
                                  setProject((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          members: (prev.members || []).filter((m) => m.id !== u.id),
                                        }
                                      : null
                                  );
                                } else {
                                  const errD = await res.json().catch(() => ({}));
                                  setMemberError(
                                    errD.error ||
                                      'Failed to remove member. Only admin, manager, or project lead can remove members.'
                                  );
                                }
                              } catch (err: any) {
                                setMemberError(err.message || 'Failed to remove member');
                              }
                            }}
                            className="text-[11px] text-rose-500 hover:text-rose-700 px-2 py-1 rounded hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              setMemberError(null);
                              const res = await fetch(`/api/projects/${projectId}/members`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({ userId: u.id, role: 'member' }),
                              });
                              if (res.ok) {
                                setProject((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        members: [...(prev.members || []), u],
                                      }
                                    : null
                                );
                              } else {
                                const errD = await res.json().catch(() => ({}));
                                setMemberError(errD.error || 'Failed to add member');
                              }
                            } catch (err: any) {
                              setMemberError(err.message || 'Failed to add member');
                            }
                          }}
                          className="px-3 py-1 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                        >
                          + Add to Project
                        </button>
                      )}
                    </div>
                  );
                }))}
            </div>
          </div>
        </Modal>
      )}

      {/* 2. Add Feature Modal (Items 7 & 18) */}
      {isAddFeatureModalOpen && (
        <Modal
          isOpen={isAddFeatureModalOpen}
          onClose={() => setIsAddFeatureModalOpen(false)}
          title="Create New Feature / Module"
          maxWidth="md"
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newFeatureName.trim() || !token) return;
              setIsSubmittingFeature(true);
              try {
                const res = await fetch(`/api/projects/${projectId}/features`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    name: newFeatureName.trim(),
                    code: newFeatureCode.trim() || undefined,
                    description: newFeatureDesc.trim(),
                    buildId: newFeatureBuildId || undefined,
                    priority: newFeaturePriority,
                    status: newFeatureStatus,
                  }),
                });
                if (res.ok) {
                  const data = await res.json();
                  setFeatures((prev) => [...prev, data.feature]);
                  setIsAddFeatureModalOpen(false);
                  setNewFeatureName('');
                  setNewFeatureCode('');
                  setNewFeatureDesc('');
                }
              } catch (err) {
                console.error('Failed to create feature:', err);
              } finally {
                setIsSubmittingFeature(false);
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Feature Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. User Authentication & SSO"
                value={newFeatureName}
                onChange={(e) => {
                  setNewFeatureName(e.target.value);
                  if (!newFeatureCode) {
                    setNewFeatureCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase());
                  }
                }}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Feature Code
                </label>
                <input
                  type="text"
                  placeholder="e.g. AUTH"
                  value={newFeatureCode}
                  onChange={(e) => setNewFeatureCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 text-xs font-mono uppercase rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Associated Build
                </label>
                <select
                  value={newFeatureBuildId}
                  onChange={(e) => setNewFeatureBuildId(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                >
                  <option value="">None / General</option>
                  {builds.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.version} ({b.buildType})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Priority
                </label>
                <select
                  value={newFeaturePriority}
                  onChange={(e) => setNewFeaturePriority(e.target.value as PriorityLevel)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                >
                  <option value="urgent">⚡ Urgent</option>
                  <option value="high">▲ High</option>
                  <option value="medium">● Medium</option>
                  <option value="low">▼ Low</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Status
                </label>
                <select
                  value={newFeatureStatus}
                  onChange={(e) => setNewFeatureStatus(e.target.value as FeatureStatus)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="testing">Testing</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Description / Scope
              </label>
              <textarea
                rows={3}
                placeholder="Acceptance criteria, architecture notes..."
                value={newFeatureDesc}
                onChange={(e) => setNewFeatureDesc(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsAddFeatureModalOpen(false)}
                className="px-4 py-2 text-xs text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingFeature || !newFeatureName.trim()}
                className="px-5 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm disabled:opacity-50"
              >
                {isSubmittingFeature ? 'Creating...' : 'Create Feature'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* 3. Add Build Modal (Items 7 & 18) */}
      {isAddBuildModalOpen && (
        <Modal
          isOpen={isAddBuildModalOpen}
          onClose={() => setIsAddBuildModalOpen(false)}
          title="Create New Build / Release"
          maxWidth="md"
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newBuildVersion.trim() || !token) return;
              setIsSubmittingBuild(true);
              try {
                const res = await fetch(`/api/projects/${projectId}/builds`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    version: newBuildVersion.trim(),
                    buildType: newBuildType,
                    description: newBuildDesc.trim(),
                    releasedAt: newBuildReleasedAt || undefined,
                  }),
                });
                if (res.ok) {
                  const data = await res.json();
                  setBuilds((prev) => [...prev, data.build]);
                  setIsAddBuildModalOpen(false);
                  setNewBuildVersion('');
                  setNewBuildDesc('');
                  setNewBuildReleasedAt('');
                }
              } catch (err) {
                console.error('Failed to create build:', err);
              } finally {
                setIsSubmittingBuild(false);
              }
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Version / Tag <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. v1.2.0 or Hotfix-1.1.2"
                  value={newBuildVersion}
                  onChange={(e) => setNewBuildVersion(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                  Build Type
                </label>
                <select
                  value={newBuildType}
                  onChange={(e) => setNewBuildType(e.target.value as BuildType)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
                >
                  <option value="build">Build (Sprint)</option>
                  <option value="hotfix">Hotfix (Urgent Patch)</option>
                  <option value="release">Release (Production)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Release Date (Optional)
              </label>
              <input
                type="date"
                value={newBuildReleasedAt}
                onChange={(e) => setNewBuildReleasedAt(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Release Notes / Changelog
              </label>
              <textarea
                rows={3}
                placeholder="Summary of bug fixes, features included in this release..."
                value={newBuildDesc}
                onChange={(e) => setNewBuildDesc(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsAddBuildModalOpen(false)}
                className="px-4 py-2 text-xs text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingBuild || !newBuildVersion.trim()}
                className="px-5 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm disabled:opacity-50"
              >
                {isSubmittingBuild ? 'Creating...' : 'Create Build'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
